import { Layer } from './Layer';
import { SelectionManager } from './Selection';
import { HistoryManager } from './History';
import { ToolManager } from '../tools/ToolManager';
import { BackgroundRemover } from '../filters/BackgroundRemover';
import { ThresholdOptimizer } from '../filters/ThresholdOptimizer';
import { Exporter } from '../export/Exporter';
import { SaveForWebModal } from '../export/SaveForWebModal';
import { CloudflareModal } from '../ui/CloudflareModal';
import { AdjustmentsModal } from '../ui/AdjustmentsModal';
import { NewCanvasModal } from '../ui/NewCanvasModal';
import { ShortcutsModal } from '../ui/ShortcutsModal';
import { StartScreen } from '../ui/StartScreen';
import { OverlayStudioModal } from '../ui/OverlayStudioModal';
import { ProjectStorage, SerializedProject } from './ProjectStorage';
import { ToolType, ToolSettings } from '../types';

export class Editor {
  public width: number = 1000;
  public height: number = 1000;
  public layers: Layer[] = [];
  public activeLayerIndex: number = 0;
  public clipboardCanvas: HTMLCanvasElement | null = null;
  public altMenuSeq: string | null = null;
  private altMenuTimer: any = null;
  public hasDocument: boolean = false;
  public startScreen!: StartScreen;
  public overlayStudioModal!: OverlayStudioModal;

  // Canvas Viewport & Overlays
  public viewportContainer: HTMLElement;
  public mainCanvas: HTMLCanvasElement;
  public mainCtx: CanvasRenderingContext2D;
  public overlayCanvas: HTMLCanvasElement;
  public overlayCtx: CanvasRenderingContext2D;

  // Viewport navigation
  public zoom: number = 1.0;
  public panX: number = 0;
  public panY: number = 0;
  public showCheckerboard: boolean = true;

  // Managers
  public selection: SelectionManager;
  public history: HistoryManager;
  public toolManager!: ToolManager;

  // Modals
  public saveForWebModal: SaveForWebModal;
  public cloudflareModal: CloudflareModal;
  public adjustmentsModal: AdjustmentsModal;
  public newCanvasModal: NewCanvasModal;

  // Settings
  public toolSettings: ToolSettings = {
    brushSize: 24,
    brushHardness: 80,
    brushOpacity: 100,
    brushColor: '#6366f1',
    eraserSize: 32,
    eraserHardness: 90,
    smudgeStrength: 50,
    smudgeRadius: 28,
    healingRadius: 22,
    healingMode: 'blemish',
    cloneSource: null,
    wandTolerance: 32,
    wandAutoTolerance: true,
    wandContiguous: true,
    wandMode: 'cut-crop',
    wandCropPadding: 0,
    bgRemoveTolerance: 38,
    bgRemoveAutoTolerance: true,
    bucketTolerance: 25,
    bucketContiguous: true,
    cropAspect: 'free',
    textFontFamily: 'Inter',
    textFontSize: 48,
    textColor: '#ffffff',
    textBold: true,
    textItalic: false,
    textAlign: 'left',
    textOutline: false,
    textOutlineColor: '#000000',
    textOutlineWidth: 2
  };

  // UI callbacks
  public onLayersChange?: () => void;
  public onActiveLayerChange?: () => void;
  public onToolChangeCallback?: (toolId: ToolType) => void;
  public onZoomChange?: (zoom: number) => void;
  public onDocumentStateChange?: (hasDoc: boolean, w: number, h: number, locked: boolean) => void;

  public isBackgroundSizeLocked: boolean = false;
  private autoSaveTimer: any = null;

  public lastWandPoint: { x: number; y: number } | null = null;
  public wandSampleAll: boolean = false;
  private isSpacePanning = false;
  private animFrameId: number = 0;
  private marchOffset = 0;

  constructor(viewportContainer: HTMLElement) {
    this.viewportContainer = viewportContainer;

    // Create canvases (hidden until document is opened or created)
    this.mainCanvas = document.createElement('canvas');
    this.mainCanvas.className = 'main-editor-canvas';
    this.mainCanvas.style.display = 'none';
    this.mainCtx = this.mainCanvas.getContext('2d', { willReadFrequently: true })!;

    this.overlayCanvas = document.createElement('canvas');
    this.overlayCanvas.className = 'overlay-editor-canvas';
    this.overlayCanvas.style.display = 'none';
    this.overlayCtx = this.overlayCanvas.getContext('2d')!;

    this.viewportContainer.appendChild(this.mainCanvas);
    this.viewportContainer.appendChild(this.overlayCanvas);

    // Initialize Blank Start Screen
    this.startScreen = new StartScreen(this, this.viewportContainer);

    // Subsystems
    this.selection = new SelectionManager();
    this.history = new HistoryManager();
    this.saveForWebModal = new SaveForWebModal(this);
    this.cloudflareModal = new CloudflareModal(this);
    this.adjustmentsModal = new AdjustmentsModal(this);
    this.newCanvasModal = new NewCanvasModal(this);
    this.shortcutsModal = new ShortcutsModal(this);
    this.overlayStudioModal = new OverlayStudioModal(this);

    // Initialize tool manager
    this.toolManager = new ToolManager(this);

    this.setupEventListeners();
    this.startMarchingAntsLoop();
    this.startScreen.show();

    // Restore any active project saved in storage until user clicks "Start New"
    this.checkAndRestoreAutoSavedProject();
  }

  public initNewCanvas(w: number, h: number, bg: string = 'transparent') {
    this.width = w;
    this.height = h;
    this.layers = [];

    const baseLayer = new Layer(w, h, 'Layer 1');
    if (bg !== 'transparent') {
      baseLayer.fill(bg);
    }
    this.layers.push(baseLayer);
    this.activeLayerIndex = 0;

    this.hasDocument = true;
    this.isBackgroundSizeLocked = true;
    this.startScreen?.hide();
    this.mainCanvas.style.display = 'block';
    this.overlayCanvas.style.display = 'block';

    this.selection.deselect();
    this.history.clear();
    this.centerViewport();
    this.recordHistory('Create Document');
    this.notifyLayersChanged();
    this.notifyDocumentStateChanged();
    this.render();
    this.showToast(`🔒 Canvas created • Background size locked to ${w} × ${h} px`);
  }

  public notifyDocumentStateChanged() {
    if (this.onDocumentStateChange) {
      this.onDocumentStateChange(this.hasDocument, this.width, this.height, this.isBackgroundSizeLocked);
    }
  }

  /**
   * Schedule debounced auto-save of active project to local storage
   */
  public scheduleAutoSave() {
    if (!this.hasDocument || this.layers.length === 0) return;
    if (this.autoSaveTimer) {
      clearTimeout(this.autoSaveTimer);
    }
    this.autoSaveTimer = setTimeout(() => {
      this.saveProjectToStorage();
    }, 600);
  }

  /**
   * Persist project state to IndexedDB/localStorage
   */
  public async saveProjectToStorage(): Promise<void> {
    if (!this.hasDocument || this.layers.length === 0) return;
    try {
      const project: SerializedProject = {
        version: '1.0',
        name: 'Auto-Saved Project',
        width: this.width,
        height: this.height,
        timestamp: Date.now(),
        isBackgroundSizeLocked: this.isBackgroundSizeLocked,
        layers: this.layers.map(l => ({
          id: l.id,
          name: l.name,
          type: l.type,
          visible: l.visible,
          locked: l.locked,
          opacity: l.opacity,
          blendMode: l.blendMode,
          x: l.x,
          y: l.y,
          width: l.width,
          height: l.height,
          dataUri: l.canvas.toDataURL(),
          textData: l.textData
        }))
      };
      await ProjectStorage.save(project);
    } catch (err) {
      console.warn('Could not auto-save project to storage:', err);
    }
  }

  /**
   * Check for saved project on application startup and restore
   */
  public async checkAndRestoreAutoSavedProject(): Promise<boolean> {
    try {
      const saved = await ProjectStorage.load();
      if (saved && saved.layers && saved.layers.length > 0) {
        await this.restoreFromSerializedProject(saved);
        return true;
      }
    } catch (err) {
      console.warn('Auto-restore check error:', err);
    }
    return false;
  }

  /**
   * Restore document and all layers from serialized project object
   */
  public async restoreFromSerializedProject(project: SerializedProject): Promise<void> {
    this.width = project.width;
    this.height = project.height;
    this.isBackgroundSizeLocked = project.isBackgroundSizeLocked ?? true;
    this.layers = [];

    const loadPromises = project.layers.map(s => {
      return new Promise<Layer>((resolve) => {
        const layer = new Layer(s.width, s.height, s.name, s.type, s.id);
        layer.visible = s.visible;
        layer.locked = s.locked;
        layer.opacity = s.opacity;
        layer.blendMode = s.blendMode;
        layer.x = s.x;
        layer.y = s.y;
        layer.textData = s.textData ? { ...s.textData } : undefined;

        if (s.dataUri) {
          const img = new Image();
          img.onload = () => {
            layer.ctx.drawImage(img, 0, 0);
            resolve(layer);
          };
          img.onerror = () => resolve(layer);
          img.src = s.dataUri;
        } else {
          resolve(layer);
        }
      });
    });

    const loadedLayers = await Promise.all(loadPromises);
    this.layers = loadedLayers;
    this.activeLayerIndex = Math.max(0, this.layers.length - 1);

    this.hasDocument = true;
    this.startScreen?.hide();
    this.mainCanvas.style.display = 'block';
    this.overlayCanvas.style.display = 'block';

    this.selection.deselect();
    this.history.clear();
    this.recordHistory('Restore Session');
    this.centerViewport();
    this.notifyLayersChanged();
    this.notifyDocumentStateChanged();
    this.render();
    this.showToast(`💾 Resumed auto-saved project (${this.width} × ${this.height} px, ${this.layers.length} layers)`);
  }

  /**
   * Clears saved project from local storage and starts completely fresh
   */
  public async startNew(confirmFirst: boolean = true) {
    if (confirmFirst && this.hasDocument && this.layers.length > 0) {
      const ok = confirm(
        'Start a new project?\n\n' +
        'This will clear your currently saved project from local storage and return to a blank screen.'
      );
      if (!ok) return;
    }

    if (this.autoSaveTimer) {
      clearTimeout(this.autoSaveTimer);
      this.autoSaveTimer = null;
    }

    await ProjectStorage.clear();
    this.closeDocument();
    this.showToast('✨ Started new session. Local storage cleared.');
  }

  public centerViewport() {
    const rect = this.viewportContainer.getBoundingClientRect();
    const pad = 60;
    const availW = Math.max(200, rect.width - pad * 2);
    const availH = Math.max(200, rect.height - pad * 2);

    const fitZoom = Math.min(availW / this.width, availH / this.height, 1.0);
    this.zoom = Math.round(fitZoom * 100) / 100;

    this.panX = Math.round((rect.width - this.width * this.zoom) / 2);
    this.panY = Math.round((rect.height - this.height * this.zoom) / 2);

    this.updateCanvasDimensions();
    if (this.onZoomChange) this.onZoomChange(this.zoom);
  }

  public updateCanvasDimensions() {
    const rect = this.viewportContainer.getBoundingClientRect();
    const vw = Math.max(300, rect.width);
    const vh = Math.max(300, rect.height);

    if (this.mainCanvas.width !== vw || this.mainCanvas.height !== vh) {
      this.mainCanvas.width = vw;
      this.mainCanvas.height = vh;
      this.overlayCanvas.width = vw;
      this.overlayCanvas.height = vh;
    }
  }

  public getActiveLayer(): Layer | null {
    if (this.activeLayerIndex >= 0 && this.activeLayerIndex < this.layers.length) {
      return this.layers[this.activeLayerIndex];
    }
    return null;
  }

  public setActiveLayer(id: string) {
    const idx = this.layers.findIndex(l => l.id === id);
    if (idx !== -1) {
      this.activeLayerIndex = idx;
      if (this.onActiveLayerChange) this.onActiveLayerChange();
      this.render();
    }
  }

  public addNewLayer(name?: string, type: 'raster' | 'text' = 'raster'): Layer {
    const layerNum = this.layers.length + 1;
    const layer = new Layer(this.width, this.height, name || `Layer ${layerNum}`, type);
    this.layers.push(layer);
    this.activeLayerIndex = this.layers.length - 1;
    this.recordHistory('New Layer');
    this.notifyLayersChanged();
    this.render();
    return layer;
  }

  public duplicateActiveLayer() {
    const active = this.getActiveLayer();
    if (!active) return;
    const dup = active.clone();
    this.layers.splice(this.activeLayerIndex + 1, 0, dup);
    this.activeLayerIndex++;
    this.recordHistory('Duplicate Layer');
    this.notifyLayersChanged();
    this.render();
  }

  public deleteActiveLayer() {
    if (this.layers.length <= 1) {
      this.showToast('Cannot delete the only layer');
      return;
    }
    this.layers.splice(this.activeLayerIndex, 1);
    this.activeLayerIndex = Math.max(0, this.activeLayerIndex - 1);
    this.recordHistory('Delete Layer');
    this.notifyLayersChanged();
    this.render();
  }

  public mergeDown() {
    if (this.activeLayerIndex <= 0) {
      this.showToast('Cannot merge down bottom layer');
      return;
    }
    const top = this.layers[this.activeLayerIndex];
    const bottom = this.layers[this.activeLayerIndex - 1];

    // Merge top onto bottom
    bottom.ctx.save();
    bottom.ctx.globalAlpha = top.opacity;
    bottom.ctx.globalCompositeOperation = top.blendMode;
    bottom.ctx.drawImage(top.canvas, top.x - bottom.x, top.y - bottom.y);
    bottom.ctx.restore();

    this.layers.splice(this.activeLayerIndex, 1);
    this.activeLayerIndex--;
    this.recordHistory('Merge Down');
    this.notifyLayersChanged();
    this.render();
  }

  public toggleTransparentBackground() {
    this.showCheckerboard = !this.showCheckerboard;
    this.showToast(this.showCheckerboard ? 'Checkerboard transparency ON' : 'Solid canvas backdrop ON');
    this.render();
  }

  public addTextLayer(text: string, x: number, y: number, font: string, size: number, color: string): Layer {
    const layer = new Layer(this.width, this.height, `Text: ${text.slice(0, 10)}`, 'text');
    layer.x = x;
    layer.y = y;
    layer.textData = {
      text,
      fontFamily: font,
      fontSize: size,
      color,
      bold: this.toolSettings.textBold,
      italic: this.toolSettings.textItalic,
      align: this.toolSettings.textAlign
    };
    layer.renderText();
    this.layers.push(layer);
    this.activeLayerIndex = this.layers.length - 1;
    this.notifyLayersChanged();
    return layer;
  }

  public setForegroundColor(color: string) {
    this.toolSettings.brushColor = color;
    this.toolSettings.textColor = color;
    if (this.toolManager.activeTool.id === 'brush' || this.toolManager.activeTool.id === 'text') {
      this.onToolChange(this.toolManager.activeTool.id);
    }
  }

  public setTool(toolId: ToolType) {
    this.toolManager.setTool(toolId);
  }

  public onToolChange(toolId: ToolType) {
    if (this.onToolChangeCallback) {
      this.onToolChangeCallback(toolId);
    }
    this.renderOverlayOnly();
  }

  public updateCursor() {
    this.overlayCanvas.style.cursor = this.toolManager.activeTool.cursor;
  }

  public autoRemoveBackground(tolerance?: number) {
    const layer = this.getActiveLayer();
    if (!layer || layer.locked) {
      this.showToast('Please select an unlocked layer to edit');
      return;
    }

    const isAuto = tolerance === undefined ? this.toolSettings.bgRemoveAutoTolerance : false;
    const tol = tolerance ?? (this.toolSettings.bgRemoveTolerance || 38);

    this.showToast(isAuto ? '⚡ Auto-Optimizing threshold & removing background...' : 'Removing background...');
    setTimeout(() => {
      const result = BackgroundRemover.removeBackground(layer, {
        autoTolerance: isAuto,
        manualTolerance: tol
      });
      this.recordHistory('Remove Background');
      this.notifyLayersChanged();
      this.render();
      this.showToast(
        result.isAuto
          ? `⚡ Auto-Optimized: Background removed (Tol: ${result.usedTolerance}, ${result.removedPixels.toLocaleString()} px cleared)`
          : `Background removed (${result.removedPixels.toLocaleString()} px cleared to transparent)`
      );
    }, 40);
  }

  public removeColorAtPoint(canvasX: number, canvasY: number) {
    const activeLayer = this.getActiveLayer();
    if (!activeLayer || activeLayer.locked) {
      this.showToast('Please select an unlocked layer to edit');
      return;
    }

    const isAuto = this.toolSettings.bgRemoveAutoTolerance;
    const tol = this.toolSettings.bgRemoveTolerance || 38;

    const result = BackgroundRemover.removeColorAtPoint(
      activeLayer,
      canvasX,
      canvasY,
      tol,
      true,
      isAuto
    );

    if (result.removedPixels > 0) {
      this.recordHistory('Magic Eraser');
      this.notifyLayersChanged();
      this.render();
      this.showToast(
        result.isAuto
          ? `⚡ Auto Eraser: Background removed (Tol: ${result.usedTolerance})`
          : `Erased background (Tolerance: ${result.usedTolerance})`
      );
    } else {
      this.showToast('No matching background area found at click position');
    }
  }

  public flipHorizontal() {
    const layer = this.getActiveLayer();
    if (!layer) return;
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = layer.width;
    tempCanvas.height = layer.height;
    const tCtx = tempCanvas.getContext('2d')!;
    tCtx.translate(layer.width, 0);
    tCtx.scale(-1, 1);
    tCtx.drawImage(layer.canvas, 0, 0);
    layer.clear();
    layer.ctx.drawImage(tempCanvas, 0, 0);
    this.recordHistory('Flip Horizontal');
    this.render();
  }

  public flipVertical() {
    const layer = this.getActiveLayer();
    if (!layer) return;
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = layer.width;
    tempCanvas.height = layer.height;
    const tCtx = tempCanvas.getContext('2d')!;
    tCtx.translate(0, layer.height);
    tCtx.scale(1, -1);
    tCtx.drawImage(layer.canvas, 0, 0);
    layer.clear();
    layer.ctx.drawImage(tempCanvas, 0, 0);
    this.recordHistory('Flip Vertical');
    this.render();
  }

  public rotate90(clockwise: boolean = true) {
    const oldW = this.width;
    const oldH = this.height;
    this.width = oldH;
    this.height = oldW;

    this.layers.forEach(layer => {
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = layer.width;
      tempCanvas.height = layer.height;
      tempCanvas.getContext('2d')!.drawImage(layer.canvas, 0, 0);

      layer.resize(layer.height, layer.width, false);
      layer.ctx.save();
      if (clockwise) {
        layer.ctx.translate(layer.width, 0);
        layer.ctx.rotate(Math.PI / 2);
      } else {
        layer.ctx.translate(0, layer.height);
        layer.ctx.rotate(-Math.PI / 2);
      }
      layer.ctx.drawImage(tempCanvas, 0, 0);
      layer.ctx.restore();
    });

    this.centerViewport();
    this.recordHistory(clockwise ? 'Rotate 90° CW' : 'Rotate 90° CCW');
    this.notifyLayersChanged();
    this.render();
  }

  public crop(x: number, y: number, w: number, h: number, historyName: string = 'Crop') {
    if (w <= 0 || h <= 0) return;

    this.layers.forEach(layer => {
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = w;
      tempCanvas.height = h;
      const tCtx = tempCanvas.getContext('2d')!;

      // Offset layer relative to crop area
      const srcX = x - layer.x;
      const srcY = y - layer.y;
      tCtx.drawImage(layer.canvas, -srcX, -srcY);

      layer.resize(w, h, false);
      layer.x = 0;
      layer.y = 0;
      layer.ctx.drawImage(tempCanvas, 0, 0);
    });

    this.width = w;
    this.height = h;
    this.centerViewport();
    this.recordHistory(historyName);
    this.notifyLayersChanged();
    this.render();
  }

  /**
   * Main rendering routine
   */
  public render() {
    this.updateCanvasDimensions();
    const ctx = this.mainCtx;
    const vw = this.mainCanvas.width;
    const vh = this.mainCanvas.height;

    // Clear viewport background
    ctx.clearRect(0, 0, vw, vh);

    if (!this.hasDocument) {
      this.renderOverlayOnly();
      return;
    }

    const cx = this.panX;
    const cy = this.panY;
    const cw = this.width * this.zoom;
    const ch = this.height * this.zoom;

    // Draw canvas drop shadow
    ctx.save();
    ctx.shadowColor = 'rgba(0, 0, 0, 0.16)';
    ctx.shadowBlur = 24;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 8;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(cx, cy, cw, ch);
    ctx.restore();

    // Draw transparency checkerboard pattern inside canvas bounds (clean light studio checkerboard)
    if (this.showCheckerboard) {
      const patternSize = Math.max(8, Math.round(12 * this.zoom));
      for (let py = 0; py < ch; py += patternSize) {
        for (let px = 0; px < cw; px += patternSize) {
          const isEven = (Math.floor(px / patternSize) + Math.floor(py / patternSize)) % 2 === 0;
          ctx.fillStyle = isEven ? '#ffffff' : '#e2e8f0';
          ctx.fillRect(
            cx + px,
            cy + py,
            Math.min(patternSize, cw - px),
            Math.min(patternSize, ch - py)
          );
        }
      }
    } else {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(cx, cy, cw, ch);
    }

    // Clip to canvas area and render layers
    ctx.save();
    ctx.beginPath();
    ctx.rect(cx, cy, cw, ch);
    ctx.clip();

    ctx.translate(cx, cy);
    ctx.scale(this.zoom, this.zoom);

    // Draw visible layers
    this.layers.forEach(layer => {
      if (!layer.visible) return;
      ctx.save();
      ctx.globalAlpha = layer.opacity;
      ctx.globalCompositeOperation = layer.blendMode;
      ctx.drawImage(layer.canvas, layer.x, layer.y);
      ctx.restore();
    });

    ctx.restore();

    this.renderOverlayOnly();
  }

  /**
   * Renders active tool overlay & selection marching ants
   */
  public renderOverlayOnly() {
    this.updateCanvasDimensions();
    const ctx = this.overlayCtx;
    ctx.clearRect(0, 0, this.overlayCanvas.width, this.overlayCanvas.height);
    if (!this.hasDocument) return;

    // Selection marching ants
    this.selection.renderOverlay(ctx, this.zoom, this.panX, this.panY, this.marchOffset);

    // Active tool overlay
    this.toolManager.activeTool.renderOverlay(ctx);
  }

  private startMarchingAntsLoop() {
    const loop = () => {
      this.marchOffset = (this.marchOffset + 0.3) % 8;
      if (this.selection.active || this.toolManager.activeTool.id === 'crop' || this.toolManager.activeTool.id === 'move') {
        this.renderOverlayOnly();
      }
      this.animFrameId = requestAnimationFrame(loop);
    };
    this.animFrameId = requestAnimationFrame(loop);
  }

  public recordHistory(actionName: string) {
    this.history.record(actionName, this.layers, this.width, this.height);
    this.scheduleAutoSave();
  }

  public undo() {
    const step = this.history.undo();
    if (step) this.restoreFromSnapshot(step);
  }

  public redo() {
    const step = this.history.redo();
    if (step) this.restoreFromSnapshot(step);
  }

  public historyJump(index: number) {
    const step = this.history.jumpTo(index);
    if (step) this.restoreFromSnapshot(step);
  }

  private restoreFromSnapshot(step: any) {
    this.width = step.canvasWidth;
    this.height = step.canvasHeight;
    this.layers = [];

    let loadedCount = 0;
    step.snapshot.forEach((s: any, idx: number) => {
      const layer = new Layer(s.width, s.height, s.name, s.type, s.id);
      layer.visible = s.visible;
      layer.locked = s.locked;
      layer.opacity = s.opacity;
      layer.blendMode = s.blendMode;
      layer.x = s.x;
      layer.y = s.y;
      layer.textData = s.textData ? { ...s.textData } : undefined;

      const img = new Image();
      img.onload = () => {
        layer.ctx.drawImage(img, 0, 0);
        loadedCount++;
        if (loadedCount === step.snapshot.length) {
          this.notifyLayersChanged();
          this.render();
        }
      };
      img.src = s.imageDataUri;
      this.layers.push(layer);
    });

    this.activeLayerIndex = Math.min(this.activeLayerIndex, this.layers.length - 1);
  }

  private notifyLayersChanged() {
    if (this.onLayersChange) this.onLayersChange();
  }

  public setZoom(newZoom: number) {
    const prevZoom = this.zoom;
    this.zoom = Math.max(0.05, Math.min(10.0, newZoom));

    // Keep center of viewport steady
    const rect = this.viewportContainer.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    this.panX = centerX - ((centerX - this.panX) / prevZoom) * this.zoom;
    this.panY = centerY - ((centerY - this.panY) / prevZoom) * this.zoom;

    if (this.onZoomChange) this.onZoomChange(this.zoom);
    this.render();
  }

  public openImageFile(file: File): Promise<void> {
    return this.openImageFileAsync(file);
  }

  public openImageFileAsync(file: File): Promise<void> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.onload = (e) => {
        const result = e.target?.result as string;
        if (!result) return resolve();
        const img = new Image();
        img.onerror = () => reject(new Error('Failed to load image'));
        img.onload = () => {
          this.openImageAsDocument(img, file.name);
          resolve();
        };
        img.src = result;
      };
      reader.readAsDataURL(file);
    });
  }

  public openImageFromDataUrl(dataUrl: string, name: string = 'Imported Image') {
    const img = new Image();
    img.onload = () => {
      this.openImageAsDocument(img, name);
    };
    img.src = dataUrl;
  }

  public openImageAsDocument(img: HTMLImageElement, name: string) {
    this.width = img.naturalWidth;
    this.height = img.naturalHeight;
    this.layers = [];

    const baseLayer = new Layer(this.width, this.height, name || 'Background');
    baseLayer.ctx.drawImage(img, 0, 0);
    this.layers.push(baseLayer);
    this.activeLayerIndex = 0;

    this.hasDocument = true;
    this.isBackgroundSizeLocked = true;
    this.startScreen?.hide();
    this.mainCanvas.style.display = 'block';
    this.overlayCanvas.style.display = 'block';

    this.selection.deselect();
    this.history.clear();
    this.recordHistory('Open ' + (name || 'Image'));
    this.centerViewport();
    this.notifyLayersChanged();
    this.notifyDocumentStateChanged();
    this.render();
    this.showToast(`🔒 Background locked to 1st photo: ${this.width} × ${this.height} px`);
  }

  public openSampleImage(url: string, name: string) {
    this.showToast(`Loading ${name}...`);
    this.openImageFromDataUrl(url, name);
  }

  public loadSampleImage(url: string, name: string) {
    this.openSampleImage(url, name);
  }

  public closeDocument() {
    this.hasDocument = false;
    this.isBackgroundSizeLocked = false;
    this.layers = [];
    this.history.clear();
    this.selection.deselect();
    this.mainCanvas.style.display = 'none';
    this.overlayCanvas.style.display = 'none';
    this.startScreen?.show();
    this.notifyLayersChanged();
    this.notifyDocumentStateChanged();
    this.render();
    this.showToast('Document closed');
  }

  public importImageAsLayer(file: File, options?: { fitToCanvas?: boolean }) {
    this.importImageAsLayerAsync(file, options);
  }

  public importImageAsLayerAsync(file: File, options?: { fitToCanvas?: boolean }): Promise<Layer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.onload = (e) => {
        const result = e.target?.result as string;
        if (!result) return reject(new Error('Empty file'));
        const img = new Image();
        img.onerror = () => reject(new Error('Failed to load image'));
        img.onload = () => {
          const fit = options?.fitToCanvas ?? true;
          const canvasW = this.width;
          const canvasH = this.height;
          const imgW = img.naturalWidth;
          const imgH = img.naturalHeight;

          let targetW = imgW;
          let targetH = imgH;

          // Scale proportionally down to fit inside locked canvas bounds if larger
          if (fit && (imgW > canvasW || imgH > canvasH)) {
            const scale = Math.min(canvasW / imgW, canvasH / imgH);
            targetW = Math.max(1, Math.round(imgW * scale));
            targetH = Math.max(1, Math.round(imgH * scale));
          }

          const layer = new Layer(targetW, targetH, file.name);
          layer.ctx.drawImage(img, 0, 0, targetW, targetH);
          layer.x = Math.round((canvasW - targetW) / 2);
          layer.y = Math.round((canvasH - targetH) / 2);

          this.layers.push(layer);
          this.activeLayerIndex = this.layers.length - 1;
          this.recordHistory('Import ' + file.name);
          this.notifyLayersChanged();
          this.render();
          this.showToast(`📸 Added "${file.name}" as layer • Background locked (${canvasW} × ${canvasH} px)`);
          resolve(layer);
        };
        img.src = result;
      };
      reader.readAsDataURL(file);
    });
  }

  public loadImageFromFile(file: File) {
    if (!this.hasDocument) {
      this.openImageFile(file);
    } else {
      this.importImageAsLayer(file, { fitToCanvas: true });
    }
  }

  public loadImageFromDataUrl(dataUrl: string, name: string = 'Imported Image') {
    if (!this.hasDocument) {
      this.openImageFromDataUrl(dataUrl, name);
    } else {
      const img = new Image();
      img.onload = () => {
        const canvasW = this.width;
        const canvasH = this.height;
        let targetW = img.naturalWidth;
        let targetH = img.naturalHeight;

        if (targetW > canvasW || targetH > canvasH) {
          const scale = Math.min(canvasW / targetW, canvasH / targetH);
          targetW = Math.max(1, Math.round(targetW * scale));
          targetH = Math.max(1, Math.round(targetH * scale));
        }

        const layer = new Layer(targetW, targetH, name);
        layer.ctx.drawImage(img, 0, 0, targetW, targetH);
        layer.x = Math.round((canvasW - targetW) / 2);
        layer.y = Math.round((canvasH - targetH) / 2);
        this.layers.push(layer);
        this.activeLayerIndex = this.layers.length - 1;
        this.recordHistory('Import ' + name);
        this.notifyLayersChanged();
        this.render();
        this.showToast(`Loaded ${name} (${img.naturalWidth}×${img.naturalHeight})`);
      };
      img.src = dataUrl;
    }
  }

  public pasteFromClipboard() {
    navigator.clipboard.read().then(items => {
      for (const item of items) {
        const imageType = item.types.find(t => t.startsWith('image/'));
        if (imageType) {
          item.getType(imageType).then(blob => {
            this.loadImageFromFile(new File([blob], 'Pasted Image.png', { type: imageType }));
          });
          return;
        }
      }
      this.showToast('No image in clipboard to paste');
    }).catch(() => {
      this.showToast('Press Ctrl+V to paste from clipboard');
    });
  }

  public exportImage(format: any) {
    Exporter.exportImage(this, format, 'ironcrop-export');
    this.showToast(`Exported image as ${format.toUpperCase()}`);
  }

  public saveProject() {
    Exporter.exportProject(this, 'project');
    this.showToast('Project saved as .photodrb');
  }

  public loadProjectFromFile(file: File) {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const project = JSON.parse(e.target?.result as string);
        if (project.layers && project.width && project.height) {
          this.width = project.width;
          this.height = project.height;
          this.restoreFromSnapshot({
            canvasWidth: project.width,
            canvasHeight: project.height,
            snapshot: project.layers.map((l: any) => ({
              ...l,
              imageDataUri: l.dataUri
            }))
          });
          this.centerViewport();
          this.recordHistory('Open Project');
          this.showToast(`Opened project ${project.name}`);
        }
      } catch (err) {
        this.showToast('Invalid project file');
      }
    };
    reader.readAsText(file);
  }

  public openSaveForWebModal() {
    this.saveForWebModal.open();
  }

  public openCloudflareModal() {
    this.cloudflareModal.open();
  }

  public openAdjustmentsModal() {
    this.adjustmentsModal.open();
  }

  public openNewCanvasModal() {
    this.newCanvasModal.open();
  }

  public openShortcutsModal() {
    this.shortcutsModal.open();
  }

  public openOverlayStudioModal() {
    this.overlayStudioModal.open();
  }

  public copySelectionOrLayer() {
    const layer = this.getActiveLayer();
    if (!layer) return;

    const copyCanvas = document.createElement('canvas');
    if (this.selection.active && this.selection.width > 0 && this.selection.height > 0) {
      copyCanvas.width = this.selection.width;
      copyCanvas.height = this.selection.height;
      const ctx = copyCanvas.getContext('2d')!;

      const lx = this.selection.x - layer.x;
      const ly = this.selection.y - layer.y;
      ctx.drawImage(layer.canvas, -lx, -ly);

      if (this.selection.mask) {
        const imgData = ctx.getImageData(0, 0, copyCanvas.width, copyCanvas.height);
        const data = imgData.data;
        for (let y = 0; y < copyCanvas.height; y++) {
          for (let x = 0; x < copyCanvas.width; x++) {
            const gx = this.selection.x + x;
            const gy = this.selection.y + y;
            const maskIdx = gy * this.selection.maskWidth + gx;
            if (this.selection.mask[maskIdx] === 0) {
              data[(y * copyCanvas.width + x) * 4 + 3] = 0;
            }
          }
        }
        ctx.putImageData(imgData, 0, 0);
      }
    } else {
      copyCanvas.width = layer.width;
      copyCanvas.height = layer.height;
      copyCanvas.getContext('2d')!.drawImage(layer.canvas, 0, 0);
    }

    this.clipboardCanvas = copyCanvas;

    copyCanvas.toBlob(blob => {
      if (blob && navigator.clipboard && navigator.clipboard.write) {
        navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]).catch(() => {});
      }
    });

    this.showToast('Copied to clipboard (Ctrl+C)');
  }

  public cutSelectionOrLayer() {
    const layer = this.getActiveLayer();
    if (!layer || layer.locked) return;

    this.copySelectionOrLayer();

    if (this.selection.active) {
      this.selection.clearSelection(layer);
    } else {
      layer.clear();
    }

    this.recordHistory('Cut');
    this.notifyLayersChanged();
    this.render();
    this.showToast('Cut selection (Ctrl+X)');
  }

  public pasteClipboard() {
    if (this.clipboardCanvas) {
      const layer = new Layer(this.clipboardCanvas.width, this.clipboardCanvas.height, 'Pasted Layer');
      layer.ctx.drawImage(this.clipboardCanvas, 0, 0);
      layer.x = Math.round((this.width - this.clipboardCanvas.width) / 2);
      layer.y = Math.round((this.height - this.clipboardCanvas.height) / 2);
      this.layers.push(layer);
      this.activeLayerIndex = this.layers.length - 1;
      this.recordHistory('Paste Layer');
      this.notifyLayersChanged();
      this.render();
      this.showToast('Pasted layer (Ctrl+V)');
      return;
    }

    this.pasteFromClipboard();
  }

  public cropToSelection() {
    if (this.selection.active && this.selection.width > 2 && this.selection.height > 2) {
      this.crop(this.selection.x, this.selection.y, this.selection.width, this.selection.height);
      this.selection.deselect();
      this.showToast('Cropped to selection (Alt+I+P)');
    } else {
      this.setTool('crop');
      this.showToast('Crop Tool activated (Alt+I+P)');
    }
  }

  public setWandMode(mode: 'select' | 'cut-crop') {
    this.toolSettings.wandMode = mode;
    this.onToolChange('wand');
  }

  public toggleWandMode() {
    const next = this.toolSettings.wandMode === 'cut-crop' ? 'select' : 'cut-crop';
    this.setWandMode(next);
    this.setTool('wand');
    this.showToast(
      next === 'cut-crop'
        ? '✂️ Magic Wand: Remove BG & Crop Mode (Default)'
        : '🪄 Magic Wand: Standard Selection Mode (Shift+W)'
    );
  }

  public trimTransparentBorders(padding: number = 0) {
    // 1. Build composite representation of visible layers
    const compositeCanvas = document.createElement('canvas');
    compositeCanvas.width = this.width;
    compositeCanvas.height = this.height;
    const compCtx = compositeCanvas.getContext('2d', { willReadFrequently: true })!;

    this.layers.forEach(layer => {
      if (layer.visible) {
        compCtx.save();
        compCtx.globalAlpha = layer.opacity;
        compCtx.globalCompositeOperation = layer.blendMode;
        compCtx.drawImage(layer.canvas, layer.x, layer.y);
        compCtx.restore();
      }
    });

    const compImgData = compCtx.getImageData(0, 0, this.width, this.height);
    const compData = compImgData.data;

    // 2. Run Connected Component Island Purge to identify the main subject and eliminate disconnected corner/border noise
    const purge = ThresholdOptimizer.purgeEdgeArtifacts(compData, this.width, this.height, undefined, 15);

    // If edge artifacts were detected and purged in the composite, clear those same pixels in visible layers
    if (purge.purgedPixels > 0) {
      for (const layer of this.layers) {
        if (!layer.visible || layer.locked) continue;
        const lImgData = layer.ctx.getImageData(0, 0, layer.width, layer.height);
        const lData = lImgData.data;
        let modified = false;
        for (let y = 0; y < layer.height; y++) {
          const cy = layer.y + y;
          if (cy < 0 || cy >= this.height) continue;
          for (let x = 0; x < layer.width; x++) {
            const cx = layer.x + x;
            if (cx < 0 || cx >= this.width) continue;
            const cIdx = (cy * this.width + cx) * 4;
            if (compData[cIdx + 3] === 0) {
              const lIdx = (y * layer.width + x) * 4;
              if (lData[lIdx + 3] > 0) {
                lData[lIdx + 3] = 0;
                modified = true;
              }
            }
          }
        }
        if (modified) {
          layer.ctx.putImageData(lImgData, 0, 0);
        }
      }
    }

    const bbox = purge.boundingBox;
    if (!bbox || bbox.maxX < bbox.minX || bbox.maxY < bbox.minY) {
      this.showToast('No opaque content to crop to (canvas is transparent)');
      return;
    }

    const { minX, maxX, minY, maxY } = bbox;
    const pad = Math.max(0, padding || this.toolSettings.wandCropPadding || 0);
    const cropX = Math.max(0, minX - pad);
    const cropY = Math.max(0, minY - pad);
    const cropW = Math.min(this.width - cropX, maxX - minX + 1 + pad * 2);
    const cropH = Math.min(this.height - cropY, maxY - minY + 1 + pad * 2);

    if (cropW <= 0 || cropH <= 0 || (cropX === 0 && cropY === 0 && cropW === this.width && cropH === this.height)) {
      this.showToast('Image borders are already trimmed (no empty border space)');
      return;
    }

    this.selection.deselect();
    this.crop(cropX, cropY, cropW, cropH, 'Trim Transparent Borders');
    this.showToast(`📐 Trimmed transparent borders to ${cropW}×${cropH}px`);
  }

  public setWandAutoTolerance(val: boolean) {
    this.toolSettings.wandAutoTolerance = val;
    this.onToolChange('wand');
    this.showToast(val ? '⚡ Auto Threshold Optimizer: ON' : 'Manual Threshold: ON');
  }

  public toggleWandAutoTolerance() {
    this.setWandAutoTolerance(!this.toolSettings.wandAutoTolerance);
  }

  public setBgRemoveAutoTolerance(val: boolean) {
    this.toolSettings.bgRemoveAutoTolerance = val;
    this.onToolChange('bg-remove');
    this.showToast(val ? '⚡ Auto Threshold Optimizer: ON' : 'Manual Threshold: ON');
  }

  public executeWandCutAndCrop(canvasX: number, canvasY: number, padding: number = 0) {
    const activeLayer = this.getActiveLayer();
    if (!activeLayer || activeLayer.locked) {
      this.showToast('Please select an unlocked layer to edit');
      return;
    }

    const sampleCanvas = document.createElement('canvas');
    sampleCanvas.width = this.width;
    sampleCanvas.height = this.height;
    const sCtx = sampleCanvas.getContext('2d', { willReadFrequently: true })!;

    if (this.wandSampleAll) {
      this.layers.forEach(layer => {
        if (layer.visible) {
          sCtx.save();
          sCtx.globalAlpha = layer.opacity;
          sCtx.globalCompositeOperation = layer.blendMode;
          sCtx.drawImage(layer.canvas, layer.x, layer.y);
          sCtx.restore();
        }
      });
    } else {
      sCtx.drawImage(activeLayer.canvas, activeLayer.x, activeLayer.y);
    }

    // High-precision edge-preserving flood fill with automatic threshold optimizer
    const floodResult = ThresholdOptimizer.smartFlood(
      sCtx,
      this.width,
      this.height,
      [{ x: canvasX, y: canvasY }],
      {
        autoTolerance: this.toolSettings.wandAutoTolerance,
        manualTolerance: this.toolSettings.wandTolerance,
        contiguous: this.toolSettings.wandContiguous,
        featherEdge: true
      }
    );

    if (floodResult.selectedCount === 0) {
      this.showToast('No matching background area found at click position');
      return;
    }

    const layersToClear = this.wandSampleAll
      ? this.layers.filter(l => l.visible && !l.locked)
      : [activeLayer];

    for (const layer of layersToClear) {
      const imgData = layer.ctx.getImageData(0, 0, layer.width, layer.height);
      const data = imgData.data;
      const mask = floodResult.mask;
      for (let i = 0; i < mask.length; i++) {
        if (mask[i] === 255) {
          data[i * 4 + 3] = 0;
        }
      }
      layer.ctx.putImageData(imgData, 0, 0);
    }

    this.selection.deselect();
    this.recordHistory('Remove Background & Crop');

    // 2-Pass Auto-Crop: runs CCA Island Purge to wipe any corner/edge remnants and crops tightly to main subject!
    this.trimTransparentBorders(padding || this.toolSettings.wandCropPadding || 0);

    const tolLabel = floodResult.isAuto ? `⚡ Auto Tol: ${floodResult.usedTolerance}` : `Tol: ${floodResult.usedTolerance}`;
    this.showToast(`✂️ Removed background (${tolLabel}) & auto-cropped canvas`);
  }

  public autoRemoveBgAndCrop(tolerance?: number, padding: number = 0) {
    const activeLayer = this.getActiveLayer();
    if (!activeLayer || activeLayer.locked) {
      this.showToast('Please select an unlocked layer to edit');
      return;
    }

    this.showToast('⚡ Analyzing image & auto-optimizing threshold...');
    setTimeout(() => {
      const sampleCanvas = document.createElement('canvas');
      sampleCanvas.width = this.width;
      sampleCanvas.height = this.height;
      const sCtx = sampleCanvas.getContext('2d', { willReadFrequently: true })!;

      this.layers.forEach(layer => {
        if (layer.visible) {
          sCtx.save();
          sCtx.globalAlpha = layer.opacity;
          sCtx.globalCompositeOperation = layer.blendMode;
          sCtx.drawImage(layer.canvas, layer.x, layer.y);
          sCtx.restore();
        }
      });

      const seeds = [
        { x: 2, y: 2 },
        { x: this.width - 3, y: 2 },
        { x: 2, y: this.height - 3 },
        { x: this.width - 3, y: this.height - 3 },
        { x: Math.floor(this.width / 2), y: 2 },
        { x: 2, y: Math.floor(this.height / 2) },
        { x: this.width - 3, y: Math.floor(this.height / 2) },
        { x: Math.floor(this.width / 2), y: this.height - 3 }
      ];

      const isAuto = tolerance === undefined ? this.toolSettings.wandAutoTolerance : false;
      const tol = tolerance ?? (this.toolSettings.wandTolerance || 32);

      const floodResult = ThresholdOptimizer.smartFlood(
        sCtx,
        this.width,
        this.height,
        seeds,
        {
          autoTolerance: isAuto,
          manualTolerance: tol,
          contiguous: true,
          featherEdge: true
        }
      );

      const layersToClear = this.wandSampleAll
        ? this.layers.filter(l => l.visible && !l.locked)
        : [activeLayer];

      for (const layer of layersToClear) {
        const imgData = layer.ctx.getImageData(0, 0, layer.width, layer.height);
        const data = imgData.data;
        const mask = floodResult.mask;
        for (let i = 0; i < mask.length; i++) {
          if (mask[i] === 255) {
            data[i * 4 + 3] = 0;
          }
        }
        layer.ctx.putImageData(imgData, 0, 0);
      }

      this.selection.deselect();
      this.trimTransparentBorders(padding || this.toolSettings.wandCropPadding || 0);
    }, 40);
  }

  public executeMagicWand(canvasX: number, canvasY: number, recordUndo: boolean = true) {
    if (this.toolSettings.wandMode === 'cut-crop') {
      this.executeWandCutAndCrop(canvasX, canvasY);
      return;
    }

    this.lastWandPoint = { x: canvasX, y: canvasY };

    const sampleCanvas = document.createElement('canvas');
    sampleCanvas.width = this.width;
    sampleCanvas.height = this.height;
    const sCtx = sampleCanvas.getContext('2d', { willReadFrequently: true })!;

    if (this.wandSampleAll) {
      this.layers.forEach(layer => {
        if (layer.visible) {
          sCtx.save();
          sCtx.globalAlpha = layer.opacity;
          sCtx.globalCompositeOperation = layer.blendMode;
          sCtx.drawImage(layer.canvas, layer.x, layer.y);
          sCtx.restore();
        }
      });
    } else {
      const active = this.getActiveLayer();
      if (!active || !active.visible) return;
      sCtx.drawImage(active.canvas, active.x, active.y);
    }

    const res = this.selection.magicWandSelect(
      sCtx,
      canvasX,
      canvasY,
      this.width,
      this.height,
      this.toolSettings.wandTolerance,
      this.toolSettings.wandContiguous,
      this.toolSettings.wandAutoTolerance
    );

    if (recordUndo && this.selection.active) {
      this.recordHistory('Magic Wand Selection');
      if (res.isAuto) {
        this.showToast(`⚡ Auto-Selected: Threshold optimized to ${res.usedTolerance}`);
      }
    }
    this.render();
  }

  public reapplyMagicWand(recordUndo: boolean = false) {
    if (this.lastWandPoint && this.toolSettings.wandMode === 'select') {
      this.executeMagicWand(this.lastWandPoint.x, this.lastWandPoint.y, recordUndo);
    }
  }

  public expandSelection(pixels: number = 2) {
    this.selection.expand(pixels, this.width, this.height);
    this.recordHistory('Expand Selection');
    this.render();
    this.showToast(`Expanded selection by ${pixels}px`);
  }

  public contractSelection(pixels: number = 2) {
    this.selection.contract(pixels, this.width, this.height);
    this.recordHistory('Contract Selection');
    this.render();
    this.showToast(`Contracted selection by ${pixels}px`);
  }

  public fillForeground() {
    const layer = this.getActiveLayer();
    if (!layer || layer.locked) return;

    const [r, g, b, a] = this.parseHexColor(this.toolSettings.brushColor);
    if (this.selection.active) {
      this.selection.fillSelection(layer, r, g, b, a);
    } else {
      layer.fill(this.toolSettings.brushColor);
    }
    this.recordHistory('Fill Foreground Color');
    this.notifyLayersChanged();
    this.render();
    this.showToast('Filled foreground color (Alt+Backspace)');
  }

  public fillBackground() {
    const layer = this.getActiveLayer();
    if (!layer || layer.locked) return;

    const bgPicker = document.querySelector<HTMLInputElement>('#bg-color-picker');
    const bgColor = bgPicker?.value || '#ffffff';
    const [r, g, b, a] = this.parseHexColor(bgColor);

    if (this.selection.active) {
      this.selection.fillSelection(layer, r, g, b, a);
    } else {
      layer.fill(bgColor);
    }
    this.recordHistory('Fill Background Color');
    this.notifyLayersChanged();
    this.render();
    this.showToast('Filled background color (Ctrl+Backspace)');
  }

  public duplicateLayerOrSelection() {
    const active = this.getActiveLayer();
    if (!active) return;

    if (this.selection.active && this.selection.width > 0 && this.selection.height > 0) {
      const newLayer = new Layer(this.selection.width, this.selection.height, `${active.name} copy`);
      newLayer.x = this.selection.x;
      newLayer.y = this.selection.y;

      const lx = this.selection.x - active.x;
      const ly = this.selection.y - active.y;
      newLayer.ctx.drawImage(active.canvas, -lx, -ly);

      if (this.selection.mask) {
        const imgData = newLayer.ctx.getImageData(0, 0, newLayer.width, newLayer.height);
        const data = imgData.data;
        for (let y = 0; y < newLayer.height; y++) {
          for (let x = 0; x < newLayer.width; x++) {
            const gx = this.selection.x + x;
            const gy = this.selection.y + y;
            const maskIdx = gy * this.selection.maskWidth + gx;
            if (this.selection.mask[maskIdx] === 0) {
              data[(y * newLayer.width + x) * 4 + 3] = 0;
            }
          }
        }
        newLayer.ctx.putImageData(imgData, 0, 0);
      }

      this.layers.splice(this.activeLayerIndex + 1, 0, newLayer);
      this.activeLayerIndex++;
      this.recordHistory('Layer via Copy');
      this.notifyLayersChanged();
      this.render();
      this.showToast('Layer via Copy (Ctrl+J)');
    } else {
      this.duplicateActiveLayer();
      this.showToast('Duplicated layer (Ctrl+J)');
    }
  }

  public flattenImage() {
    if (this.layers.length <= 1) return;
    const flatCanvas = Exporter.getCompositeCanvas(this, this.width, this.height, this.showCheckerboard);
    const baseLayer = new Layer(this.width, this.height, 'Background');
    baseLayer.ctx.drawImage(flatCanvas, 0, 0);
    this.layers = [baseLayer];
    this.activeLayerIndex = 0;
    this.recordHistory('Flatten Image');
    this.notifyLayersChanged();
    this.render();
    this.showToast('Flattened all layers (Ctrl+Shift+E)');
  }

  public desaturateActiveLayer() {
    const layer = this.getActiveLayer();
    if (!layer || layer.locked) return;
    const imgData = layer.ctx.getImageData(0, 0, layer.width, layer.height);
    const data = imgData.data;
    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] === 0) continue;
      const gray = Math.round(0.2989 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]);
      data[i] = gray;
      data[i + 1] = gray;
      data[i + 2] = gray;
    }
    layer.ctx.putImageData(imgData, 0, 0);
    this.recordHistory('Desaturate');
    this.notifyLayersChanged();
    this.render();
    this.showToast('Desaturated layer (Ctrl+Shift+U)');
  }

  public invertActiveLayer() {
    const layer = this.getActiveLayer();
    if (!layer || layer.locked) return;
    const imgData = layer.ctx.getImageData(0, 0, layer.width, layer.height);
    const data = imgData.data;
    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] === 0) continue;
      data[i] = 255 - data[i];
      data[i + 1] = 255 - data[i + 1];
      data[i + 2] = 255 - data[i + 2];
    }
    layer.ctx.putImageData(imgData, 0, 0);
    this.recordHistory('Invert Colors');
    this.notifyLayersChanged();
    this.render();
    this.showToast('Inverted layer colors (Ctrl+I)');
  }

  public swapColors() {
    const fgPicker = document.querySelector<HTMLInputElement>('#fg-color-picker');
    const bgPicker = document.querySelector<HTMLInputElement>('#bg-color-picker');
    if (fgPicker && bgPicker) {
      const temp = fgPicker.value;
      fgPicker.value = bgPicker.value;
      bgPicker.value = temp;
      this.setForegroundColor(fgPicker.value);
      this.showToast('Swapped colors (X)');
    }
  }

  public resetColors() {
    this.setForegroundColor('#000000');
    const bgPicker = document.querySelector<HTMLInputElement>('#bg-color-picker');
    if (bgPicker) bgPicker.value = '#ffffff';
    this.showToast('Reset default B&W colors (D)');
  }

  private parseHexColor(hex: string): [number, number, number, number] {
    let c = hex.replace('#', '');
    if (c.length === 3) {
      c = c.split('').map(x => x + x).join('');
    }
    const num = parseInt(c, 16);
    return [(num >> 16) & 255, (num >> 8) & 255, num & 255, 255];
  }

  public showToast(msg: string) {
    let toast = document.querySelector('.studio-toast') as HTMLElement;
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'studio-toast';
      document.body.appendChild(toast);
    }
    toast.textContent = msg;
    toast.classList.add('visible');
    setTimeout(() => {
      toast.classList.remove('visible');
    }, 2800);
  }

  private setupEventListeners() {
    // Window Resize
    window.addEventListener('resize', () => {
      this.render();
    });

    // Mouse coordinates converter
    const getCanvasCoords = (e: MouseEvent) => {
      const rect = this.viewportContainer.getBoundingClientRect();
      const screenX = e.clientX - rect.left;
      const screenY = e.clientY - rect.top;
      const canvasX = (screenX - this.panX) / this.zoom;
      const canvasY = (screenY - this.panY) / this.zoom;
      return { canvasX, canvasY, screenX, screenY };
    };

    // Viewport mouse interactions
    this.overlayCanvas.addEventListener('mousedown', (e) => {
      if (e.button === 1 || this.isSpacePanning) {
        this.toolManager.getTool('hand')?.onMouseDown(e, 0, 0);
        return;
      }
      const { canvasX, canvasY } = getCanvasCoords(e);
      this.toolManager.activeTool.onMouseDown(e, canvasX, canvasY);
    });

    window.addEventListener('mousemove', (e) => {
      const { canvasX, canvasY } = getCanvasCoords(e);
      const coordDisplay = document.querySelector('#status-coords');
      if (coordDisplay) {
        coordDisplay.textContent = `${Math.round(canvasX)}, ${Math.round(canvasY)} px`;
      }
      this.toolManager.activeTool.onMouseMove(e, canvasX, canvasY);
    });

    window.addEventListener('mouseup', (e) => {
      const { canvasX, canvasY } = getCanvasCoords(e);
      this.toolManager.activeTool.onMouseUp(e, canvasX, canvasY);
    });

    // Zoom on wheel (Ctrl + Wheel or standard wheel)
    this.viewportContainer.addEventListener('wheel', (e) => {
      e.preventDefault();
      const zoomFactor = e.deltaY < 0 ? 1.15 : 0.87;
      this.setZoom(this.zoom * zoomFactor);
    }, { passive: false });

    // Drag and Drop files onto canvas
    const dropZone = this.viewportContainer;
    let dragCounter = 0;

    window.addEventListener('dragenter', (e) => {
      e.preventDefault();
      dragCounter++;
      dropZone.classList.add('drag-over');
    });

    window.addEventListener('dragleave', (e) => {
      e.preventDefault();
      dragCounter--;
      if (dragCounter <= 0) {
        dragCounter = 0;
        dropZone.classList.remove('drag-over');
      }
    });

    window.addEventListener('dragover', (e) => {
      e.preventDefault();
    });

    window.addEventListener('drop', async (e) => {
      e.preventDefault();
      dragCounter = 0;
      dropZone.classList.remove('drag-over');

      const files = e.dataTransfer?.files;
      if (!files || files.length === 0) return;

      const fileList = Array.from(files);
      const projFile = fileList.find(f => f.name.endsWith('.photodrb'));
      if (projFile) {
        this.loadProjectFromFile(projFile);
        return;
      }

      const imageFiles = fileList.filter(f => f.type.startsWith('image/'));
      if (imageFiles.length === 0) return;

      if (!this.hasDocument) {
        // Drag 1st photo: locks the background canvas size to this photo
        const first = imageFiles[0];
        try {
          await this.openImageFileAsync(first);
          // Any remaining photos in the drop are placed as layers on this locked canvas
          for (let i = 1; i < imageFiles.length; i++) {
            await this.importImageAsLayerAsync(imageFiles[i], { fitToCanvas: true });
          }
        } catch (err) {
          console.error('Error opening 1st photo:', err);
        }
      } else {
        // Canvas size was already chosen or locked: do NOT change background size!
        for (let i = 0; i < imageFiles.length; i++) {
          try {
            await this.importImageAsLayerAsync(imageFiles[i], { fitToCanvas: true });
          } catch (err) {
            console.error('Error importing dropped photo as layer:', err);
          }
        }
      }
    });

    // Paste from clipboard (Ctrl+V)
    window.addEventListener('paste', (e: ClipboardEvent) => {
      if ((e.target as HTMLElement).tagName === 'INPUT' || (e.target as HTMLElement).tagName === 'TEXTAREA') {
        return;
      }
      this.pasteClipboard();
    });

    // Global Photoshop Keyboard Shortcuts & Accelerator Sequences
    window.addEventListener('keydown', (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).tagName === 'INPUT' || (e.target as HTMLElement).tagName === 'TEXTAREA') {
        return;
      }

      const keyLower = e.key.toLowerCase();
      const keyUpper = e.key.toUpperCase();

      // Photoshop Menu Accelerator: Alt+I+P (Image -> Crop)
      if (e.altKey && keyLower === 'i') {
        e.preventDefault();
        this.altMenuSeq = 'I';
        if (this.altMenuTimer) clearTimeout(this.altMenuTimer);
        this.altMenuTimer = setTimeout(() => { this.altMenuSeq = null; }, 2000);
        this.showToast('Image menu accelerator active (press P for Crop)...');
        return;
      }

      if (this.altMenuSeq === 'I') {
        if (keyLower === 'p') {
          e.preventDefault();
          this.cropToSelection();
          this.altMenuSeq = null;
          return;
        } else if (keyLower === 'r') {
          e.preventDefault();
          this.rotate90(true);
          this.altMenuSeq = null;
          return;
        } else if (keyLower === 'h') {
          e.preventDefault();
          this.flipHorizontal();
          this.altMenuSeq = null;
          return;
        } else if (keyLower === 'v') {
          e.preventDefault();
          this.flipVertical();
          this.altMenuSeq = null;
          return;
        }
      }

      // Spacebar panning (Hand tool)
      if (e.code === 'Space' && !this.isSpacePanning) {
        this.isSpacePanning = true;
        this.overlayCanvas.style.cursor = 'grab';
      }

      // Alt+Backspace (Fill Foreground) / Ctrl+Backspace (Fill Background)
      if (e.altKey && (e.key === 'Backspace' || e.key === 'Delete')) {
        e.preventDefault();
        this.fillForeground();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'Backspace' || e.key === 'Delete')) {
        e.preventDefault();
        this.fillBackground();
        return;
      }

      // Bracket keys [ and ] for brush / tool size
      if (!e.ctrlKey && !e.metaKey && !e.altKey) {
        if (e.key === '[') {
          e.preventDefault();
          this.adjustBrushSize(-4);
          return;
        } else if (e.key === ']') {
          e.preventDefault();
          this.adjustBrushSize(4);
          return;
        } else if (e.key === '{') { // Shift + [
          e.preventDefault();
          this.adjustBrushHardness(-10);
          return;
        } else if (e.key === '}') { // Shift + ]
          e.preventDefault();
          this.adjustBrushHardness(10);
          return;
        }

        // Opacity shortcuts 0-9
        if (/^[0-9]$/.test(e.key)) {
          const active = this.getActiveLayer();
          if (active && !active.locked) {
            const digit = parseInt(e.key);
            active.opacity = digit === 0 ? 1.0 : digit / 10;
            this.recordHistory('Change Opacity');
            this.notifyLayersChanged();
            this.render();
            this.showToast(`Layer Opacity: ${Math.round(active.opacity * 100)}%`);
          }
          return;
        }
      }

      // Help shortcuts (? or F1)
      if (e.key === '?' || e.key === 'F1') {
        e.preventDefault();
        this.openShortcutsModal();
        return;
      }

      // Trim Borders shortcut (Alt+Shift+T)
      if (e.altKey && e.shiftKey && keyUpper === 'T') {
        e.preventDefault();
        this.trimTransparentBorders();
        return;
      }

      // Photo Overlay Border & Reel Studio (Alt+Shift+B)
      if (e.altKey && e.shiftKey && keyUpper === 'B') {
        e.preventDefault();
        this.openOverlayStudioModal();
        return;
      }

      // Single-key Tool shortcuts
      if (!e.ctrlKey && !e.metaKey && !e.altKey) {
        switch (keyUpper) {
          case 'V': this.setTool('move'); break;
          case 'M': this.setTool('marquee'); break;
          case 'W':
            if (e.shiftKey) {
              e.preventDefault();
              this.toggleWandMode();
            } else {
              this.setTool('wand');
            }
            break;
          case 'C': this.setTool('crop'); break;
          case 'J': this.setTool('heal'); break;
          case 'B': this.setTool('brush'); break;
          case 'R': this.setTool('smudge'); break;
          case 'E': this.setTool('eraser'); break;
          case 'G': this.setTool('bucket'); break;
          case 'K': this.setTool('bg-remove'); break;
          case 'T': this.setTool('text'); break;
          case 'I': this.setTool('eyedropper'); break;
          case 'H': this.setTool('hand'); break;
          case 'D': this.resetColors(); break;
          case 'X': this.swapColors(); break;
        }
      }

      // Delete: clear selection
      if (e.key === 'Delete') {
        if (this.selection.active) {
          const l = this.getActiveLayer();
          if (l) {
            this.selection.clearSelection(l);
            this.recordHistory('Clear Selection');
            this.render();
          }
        }
      }

      // Ctrl / Cmd shortcuts
      if (e.ctrlKey || e.metaKey) {
        if (keyLower === 'c') {
          e.preventDefault();
          this.copySelectionOrLayer();
        } else if (keyLower === 'v') {
          e.preventDefault();
          this.pasteClipboard();
        } else if (keyLower === 'x') {
          e.preventDefault();
          this.cutSelectionOrLayer();
        } else if (keyLower === 'j') {
          e.preventDefault();
          this.duplicateLayerOrSelection();
        } else if (keyLower === 'n' && e.shiftKey) {
          e.preventDefault();
          this.addNewLayer();
        } else if (keyLower === 'n') {
          e.preventDefault();
          this.openNewCanvasModal();
        } else if (keyLower === 'o') {
          e.preventDefault();
          document.querySelector<HTMLInputElement>('input[type="file"]')?.click();
        } else if (keyLower === 'e' && e.shiftKey) {
          e.preventDefault();
          this.flattenImage();
        } else if (keyLower === 'e') {
          e.preventDefault();
          this.mergeDown();
        } else if (keyLower === 't') {
          e.preventDefault();
          this.setTool('move');
          this.showToast('Free Transform (Ctrl+T)');
        } else if (keyLower === 'u' && e.shiftKey) {
          e.preventDefault();
          this.desaturateActiveLayer();
        } else if (keyLower === 'u' || keyLower === 'l' || keyLower === 'm') {
          e.preventDefault();
          this.openAdjustmentsModal();
        } else if (keyLower === 'i' && e.shiftKey) {
          e.preventDefault();
          this.selection.invert(this.width, this.height);
          this.render();
        } else if (keyLower === 'i') {
          e.preventDefault();
          this.invertActiveLayer();
        } else if (keyLower === '0') {
          e.preventDefault();
          this.centerViewport();
        } else if (keyLower === '1') {
          e.preventDefault();
          this.setZoom(1.0);
        } else if (keyLower === '=' || keyLower === '+') {
          e.preventDefault();
          this.setZoom(this.zoom * 1.25);
        } else if (keyLower === '-') {
          e.preventDefault();
          this.setZoom(this.zoom * 0.8);
        } else if (keyLower === 'z') {
          e.preventDefault();
          if (e.shiftKey) {
            this.redo();
          } else {
            this.undo();
          }
        } else if (keyLower === 'y') {
          e.preventDefault();
          this.redo();
        } else if (keyLower === 'a') {
          e.preventDefault();
          this.selection.selectAll(this.width, this.height);
          this.render();
        } else if (keyLower === 'd') {
          e.preventDefault();
          this.selection.deselect();
          this.render();
        } else if (keyLower === 's' && e.altKey) {
          e.preventDefault();
          this.openSaveForWebModal();
        } else if (keyLower === 's') {
          e.preventDefault();
          this.exportImage('png');
        }
      }

      this.toolManager.activeTool.onKeyDown(e);
    });

    window.addEventListener('keyup', (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        this.isSpacePanning = false;
        this.updateCursor();
      }
    });
  }

  public adjustBrushSize(delta: number) {
    const s = this.toolSettings;
    const curTool = this.toolManager.activeTool.id;
    if (curTool === 'brush') {
      s.brushSize = Math.max(1, Math.min(250, s.brushSize + delta));
      this.showToast(`Brush Size: ${s.brushSize}px`);
    } else if (curTool === 'eraser') {
      s.eraserSize = Math.max(2, Math.min(250, s.eraserSize + delta));
      this.showToast(`Eraser Size: ${s.eraserSize}px`);
    } else if (curTool === 'smudge') {
      s.smudgeRadius = Math.max(4, Math.min(120, s.smudgeRadius + delta));
      this.showToast(`Smudge Radius: ${s.smudgeRadius}px`);
    } else if (curTool === 'heal') {
      s.healingRadius = Math.max(3, Math.min(100, s.healingRadius + delta));
      this.showToast(`Healing Radius: ${s.healingRadius}px`);
    }
    this.onToolChange(curTool);
  }

  public adjustBrushHardness(delta: number) {
    const s = this.toolSettings;
    const curTool = this.toolManager.activeTool.id;
    if (curTool === 'brush') {
      s.brushHardness = Math.max(0, Math.min(100, s.brushHardness + delta));
      this.showToast(`Brush Hardness: ${s.brushHardness}%`);
    } else if (curTool === 'eraser') {
      s.eraserHardness = Math.max(0, Math.min(100, s.eraserHardness + delta));
      this.showToast(`Eraser Hardness: ${s.eraserHardness}%`);
    }
    this.onToolChange(curTool);
  }
}

